import {Box} from '@mui/material'
import classNames from 'classnames';
import React from 'react'
import { useIntl } from 'react-intl';
import { useMediaQuery } from 'react-responsive';
import Calendar from './components/base-calendar';
import { DeleteDialog } from '../../../../components/shared/delete-dialog';
import CreateNewButtonV2 from '../../../../components/shared/create-new-button-v2';
import PageFooter from '../../../../components/shared/page-footer';
import PageHeader from '../../../../components/shared/page-header';
import CalendarInfo from './components/info';
import DaysInfo from './components/day/info';
import DeleteCalendarButton from './components/delete-calendar-button';
import EditCalendarButton from './components/edit-calendar-button';
import MobileInfoPopup from '../../../../components/shared/mobile-info-popup';
import styles from './work-calendar.module.scss'
import PageWrapperWorkCalendar from './components/page-wrapper';
import SettingsHeader from '../settings-header';
import useElementWidth from './hooks/use-element-width';
import useWorkCalendarActions from './hooks/use-work-calendar-actions';
import useWorkCalendarPage from './hooks/use-work-calendar-page';
import PageWrapperV2 from '../../../../components/shared/page-wrapper-v2';
import { getDesktopYearColumns } from './helpers/calendar-layout';
import { useAppSelector } from '../../../../hooks/hooks';
import { canManageWorkCalendars, canSeeOwnWorkCalendar } from '../../../../features/profile/entityPermissions';

export default function WorkCalendar() {
    const intl = useIntl();
    const profile = useAppSelector(state => state.profile);
    const canViewWorkCalendars = canSeeOwnWorkCalendar(profile);
    const canEditWorkCalendars = canManageWorkCalendars(profile);
    const isMob = useMediaQuery({ maxWidth: '1000px' });
    const {
        currentYear,
        loading,
        shouldRenderCalendar,
        workCalendars,
        workCalendarId,
        year,
        calendarConfig
    } = useWorkCalendarPage(isMob);
    const {
        deleteMessage,
        isDeleteDialogOpen,
        handleCalendarCreate,
        handleCalendarDelete,
        handleCalendarEdit,
        handleCalendarSelect,
        handleDayClick,
        handleDeleteCancel,
        handleDeleteConfirm,
    } = useWorkCalendarActions({
        currentYear,
        intl,
        workCalendarId,
        workCalendars,
        year,
    });
    const { ref: calendarRef, width: calendarWidth } = useElementWidth<HTMLDivElement>(shouldRenderCalendar, [
        calendarConfig,
        isMob
    ]);
    const { ref: pageBodyContentRef, width: pageBodyContentWidth } = useElementWidth<HTMLDivElement>(!isMob, [
        shouldRenderCalendar,
        isMob
    ]);

    const desktopYearColumns = React.useMemo(() => {
        if (isMob) {
            return 1;
        }

        return getDesktopYearColumns({ containerWidth: pageBodyContentWidth });
    }, [isMob, pageBodyContentWidth]);

    const calendarContent = shouldRenderCalendar && calendarConfig ? (
        <Box 
            ref={calendarRef}
            className={classNames({
                [styles.calendarBox]: true,
                [styles.calendarContainerWrapper]: isMob,
            })}>
            <Calendar
                key={calendarConfig.minDate.unix()}
                minDate={calendarConfig.minDate}
                maxDate={calendarConfig.maxDate}
                years={calendarConfig.years}
                view={calendarConfig.view}
                size={calendarConfig.size}
                showHeader={!isMob}
                onDateClick={canEditWorkCalendars ? handleDayClick : undefined}
                disabledNavigation={!isMob}
                yearColumns={desktopYearColumns}
                dense={false} />
        </Box>
    ) : null;

    const pageBodyWrapperClassName = classNames(styles.pageBodyWrapper, {
        [styles.pageBodyWrapperWithFooter]: isMob
    });

    if (!canViewWorkCalendars) {
        return null;
    }
    
    return (
        <PageWrapperV2>
            <SettingsHeader activeTab={'calendar'} loading={loading} />
            <div className={pageBodyWrapperClassName}>
                <PageWrapperWorkCalendar>
                    <PageHeader>
                        <div className={styles.headerGrid}>
                            <div className={styles.headerTitle}>
                                <div className={styles.calendarInfoWrapper}>
                                    <CalendarInfo 
                                        onCreateClick={handleCalendarCreate} 
                                        onEditClick={handleCalendarEdit} 
                                        onDeleteClick={handleCalendarDelete} 
                                        onSelectClick={handleCalendarSelect} 
                                        small={isMob}
                                        canManage={canEditWorkCalendars} />
                                </div>
                                {
                                    isMob &&
                                        <MobileInfoPopup>
                                            <DaysInfo />
                                        </MobileInfoPopup>
                                }
                            </div>
                        </div>
                    </PageHeader>
                    <div className={styles.pageBodyContent} ref={pageBodyContentRef}>
                        <Box sx={{ width: '100%', padding: isMob ? '0px' : '0 0 16px 0' }}>
                            <Box className={classNames({
                                [styles.content]: true,
                                [styles.small]: isMob
                            })}>
                                {!isMob && (
                                    <div
                                        className={classNames(styles.daysInfoWrapper, {
                                            [styles.daysInfoHidden]: !shouldRenderCalendar || loading,
                                        })}
                                        style={{ width: calendarWidth === 0 ? '100%' : `${calendarWidth}px` }}
                                    >
                                        <DaysInfo />
                                    </div>
                                )}
                                {calendarContent}
                            </Box>
                        </Box>
                    </div>
                    {isMob && canEditWorkCalendars &&
                        <PageFooter>
                            <div className={styles.mobileFooterButtons}>
                                <CreateNewButtonV2 onClick={handleCalendarCreate} />
                                <EditCalendarButton onClick={handleCalendarEdit} />
                                {workCalendars.length > 1 /*If not the last workCalendars */ && <DeleteCalendarButton onClick={handleCalendarDelete} />}
                            </div>
                        </PageFooter>
                    }
                </PageWrapperWorkCalendar>
                <DeleteDialog
                    open={isDeleteDialogOpen}
                    onCancel={handleDeleteCancel}
                    onConfirmDelete={handleDeleteConfirm}
                    deleteHeader={intl.formatMessage({ id: 'delete_work_calendar' })}
                    deleteMessage={deleteMessage}
                    title={intl.formatMessage({ id: 'work_calendar' })}
                />
            </div>
        </PageWrapperV2>
    );
}
